import { TRIGGER_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Trigger } from "~/entities/medical/trigger";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

interface TriggerListingItem {
    id: string;
    title: string;
    known: boolean;
}

const type = MedicalType.trigger;

export default function TriggersIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: TriggerListingItem[] = useMemo(() => {
        return listMedical(type).map((t: Trigger) => ({
            id: t.id as string,
            title: findOption(OptionType.triggers, t.option_id)?.title as string,
            known: t.known,
        }));
    }, [findOption, listMedical]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="triggers">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share triggers" initial={["triggers", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Triggers"
                    icon="zap"
                    button={() => (
                        <Can ability="update" section="triggers">
                            <Button
                                title="Add trigger"
                                variant="primary"
                                size="lg"
                                href="/health-profile/triggers/create"
                            />
                        </Can>
                    )}
                >
                    Document known and suspected triggers to help recognise patterns and avoid discomfort.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<TriggerListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        groups={[
                            { name: "Known", matches: (item) => item.known },
                            { name: "Suspected", matches: (item) => !item.known },
                        ]}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                href={{ pathname: `${TRIGGER_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
